import './assets/index.css'
import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'
import 'preline/preline'

// Import Froala Editor
import 'froala-editor/js/plugins.pkgd.min.js'
import 'froala-editor/js/third_party/font_awesome.min'
import 'froala-editor/js/third_party/image_tui.min'
import 'froala-editor/js/languages/de.js'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/froala_style.min.css'
import VueFroala from 'vue-froala-wysiwyg'

import App from './App.vue'
import router from './router'
import { ROUTES } from '@/router/routes'
import { useUnifiedChatStore } from '@/store/app/unifiedChat'
import posthog from 'posthog-js'
import type { PostHog } from 'posthog-js'
import setupPdfs from './utils/pdfjs'
import {
  setupAdminApiV3,
  setupApiV2,
  setupApiV3,
  setupSentry,
  setupTranslations
} from '@/utils/bootstrap'

declare global {
  interface Window {
    posthog?: PostHog
  }
}

const app = createApp(App)
const i18n = createI18n({ legacy: false, locale: 'en', fallbackLocale: 'en' })
// Same-origin API as admin.ts: session cookies and CSRF align with the page origin (public app,
// internal admin, or Vite dev server with /api proxied).
const API_URL = `${window.location.origin}/api/`
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const ENV = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.VITE_ENV

app.use(createPinia())
app.use(router)

// Set up API clients
setupApiV3({ apiUrl: API_URL })
setupAdminApiV3({ apiUrl: API_URL })
setupApiV2({ apiUrl: API_URL })

// Set up Sentry error tracking with PostHog integration
setupSentry({
  app,
  router,
  dsn: SENTRY_DSN,
  environment: ENV,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  additionalIntegrations: [
    posthog.sentryIntegration({
      organization: import.meta.env.SENTRY_ORGANIZATION,
      projectId: import.meta.env.SENTRY_PROJECT_ID,
      severityAllowList: ['error', 'info']
    })
  ]
})

// Set up translations
setupTranslations({ app, i18n })
app.use(VueFroala)

setupPdfs()

/**
 * Capture SPA page views via PostHog.
 *
 * PostHog is already initialized, identified, and grouped in the Django
 * base template (base.html) using server-rendered user/org data. This
 * avoids race conditions where page views fire before the user or
 * organisation has been identified.
 *
 * The SPA only needs to capture additional $pageview events for
 * client-side (Vue Router) navigations that don't trigger a full
 * page load. The initial page view is already captured by base.html,
 * so we skip the first navigation (where from has no matched routes).
 */
router.afterEach((to, from, failure) => {
  if (!failure && from.matched.length > 0) {
    nextTick(() => {
      window.posthog?.capture('$pageview', { path: to.fullPath })
    })
  }
})

// Resolve the initial URL before mounting so `useRoute()` and Pinia consumers see the final path
// (avoids tearing down UI that depends on sessionStorage, e.g. the AI side panel).
void router.isReady().then(() => {
  app.mount('#app')

  /**
   * Django ``base.html`` intercepts a second click on the AI assistant tab while already on
   * ``/app/ai-assistant`` (same-URL navigation does not reload the SPA). Re-run the same entry
   * setup as ``router.beforeEnter`` so a new empty conversation is shown.
   */
  window.addEventListener('jupus:ai-assistant-top-nav', () => {
    void (async () => {
      const ran = await useUnifiedChatStore().startNewFullscreenChat()
      if (ran) {
        void router.replace(ROUTES.APP.AI_ASSISTANT.ROOT)
      }
    })()
  })
})
